import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Tabs, Tab, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, Paper, Divider, Chip, List, ListItem, ListItemIcon,
  ListItemText, IconButton, Tooltip, Avatar, Alert, Snackbar, TextField,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StatCard from '../../components/common/StatCard';
import DataTable from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import db from '../../data/demoDatabase';
import { useAuth } from '../../context/AuthContext';

const PRIMARY = '#1B365D';
const SECONDARY = '#00B894';

// Convert an invite record to a displayable "partner" row
const inviteToRow = (inv) => ({
  id: inv.id,
  name: inv.submittedData?.name || inv.recipientEmail || inv.token,
  type: inv.role === 'partner' ? 'Corporate' : 'Individual',
  email: inv.submittedData?.email || inv.recipientEmail || '—',
  phone: inv.submittedData?.phone || '—',
  icNumber: inv.submittedData?.icNumber || '—',
  company: inv.submittedData?.company || '—',
  role: inv.role,
  documents: ['IC Copy', 'Bank Statement'],
  submittedDate: new Date(inv.createdAt).toLocaleDateString(),
  rejectedDate: inv.rejectedAt ? new Date(inv.rejectedAt).toLocaleDateString() : null,
  reason: inv.rejectionReason || '',
  status: inv.status,
  referredBy: inv.note || 'Direct',
  token: inv.token,
});

// Convert an approved user to a displayable row
const userToRow = (u) => ({
  id: u.id,
  name: u.name,
  type: u.role === 'partner' ? 'Corporate' : 'Individual',
  email: u.email,
  phone: u.phone,
  submittedDate: u.joinDate,
  approvedDate: u.joinDate,
  status: u.status,
});

export default function PartnerApproval() {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const bump = () => setRefreshKey((k) => k + 1);
  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  // Live data reads from the DB
  const invites = useMemo(() => db.invites.getAll(), [refreshKey]);
  const allUsers = useMemo(() => db.users.getAll(), [refreshKey]);

  const pendingPartners = useMemo(
    () => invites
      .filter((i) => i.status === 'submitted' && ['partner', 'individual_agent'].includes(i.role))
      .map(inviteToRow),
    [invites]
  );

  const approvedPartners = useMemo(
    () => allUsers
      .filter((u) => ['partner', 'individual_agent'].includes(u.role) && u.status === 'active')
      .map(userToRow),
    [allUsers]
  );

  const rejectedPartners = useMemo(
    () => invites
      .filter((i) => i.status === 'rejected')
      .map(inviteToRow),
    [invites]
  );

  const stats = useMemo(() => {
    const thisMonth = new Date().getMonth();
    const approvedThisMonth = allUsers.filter((u) => {
      if (!['partner', 'individual_agent'].includes(u.role)) return false;
      if (!u.joinDate) return false;
      return new Date(u.joinDate).getMonth() === thisMonth;
    }).length;
    const totalDecisions = approvedPartners.length + rejectedPartners.length;
    const rejectionRate = totalDecisions === 0 ? 0 : Math.round((rejectedPartners.length / totalDecisions) * 100);
    return {
      pending: pendingPartners.length,
      approvedThisMonth,
      rejectionRate,
    };
  }, [allUsers, approvedPartners.length, rejectedPartners.length, pendingPartners.length]);

  const handleViewDetails = (partner) => {
    setSelectedPartner(partner);
    setDetailOpen(true);
  };

  const handleApprove = (partner) => {
    if (!partner?.id) return;
    const result = db.engine.approveInvite(partner.id, user);
    if (result) {
      showSnack(`${result.user.name} approved — account created (${result.user.id})`);
      setDetailOpen(false);
      bump();
    } else {
      showSnack('Failed to approve invite', 'error');
    }
  };

  const handleRejectOpen = (partner) => {
    setSelectedPartner(partner);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = () => {
    if (!selectedPartner) return;
    db.invites.update(selectedPartner.id, {
      status: 'rejected',
      rejectionReason: rejectReason || 'Rejected by admin',
      rejectedAt: new Date().toISOString(),
    });
    db.auditLogs.create({
      userId: user?.id,
      userName: user?.name,
      userRole: user?.role,
      action: 'reject_invite',
      entityType: 'invite',
      entityId: selectedPartner.id,
      description: `Rejected invite for ${selectedPartner.name}`,
      reason: rejectReason,
    });
    showSnack(`${selectedPartner.name} rejected`, 'warning');
    setRejectDialogOpen(false);
    setDetailOpen(false);
    bump();
  };

  const pendingColumns = [
    { field: 'name', headerName: 'Name', width: 200 },
    {
      field: 'type', headerName: 'Type', width: 120,
      renderCell: (row) => (
        <Chip
          icon={row.type === 'Corporate' ? <BusinessIcon sx={{ fontSize: 14 }} /> : <PersonIcon sx={{ fontSize: 14 }} />}
          label={row.type} size="small"
          sx={{
            fontWeight: 600, fontSize: '0.75rem', borderRadius: '6px',
            bgcolor: row.type === 'Corporate' ? '#F3E5F5' : '#E3F2FD',
            color: row.type === 'Corporate' ? '#7B1FA2' : '#1565C0',
          }}
        />
      ),
    },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'phone', headerName: 'Phone', width: 160 },
    { field: 'submittedDate', headerName: 'Submitted', width: 120 },
    {
      field: 'actions', headerName: 'Actions', width: 170, sortable: false,
      renderCell: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Approve">
            <IconButton size="small" sx={{ color: '#2E7D32' }}
              onClick={(e) => { e.stopPropagation(); handleApprove(row); }}>
              <CheckCircleIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reject">
            <IconButton size="small" sx={{ color: '#C62828' }}
              onClick={(e) => { e.stopPropagation(); handleRejectOpen(row); }}>
              <CancelIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewDetails(row); }} sx={{ color: PRIMARY }}>
              <VisibilityIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const approvedColumns = [
    { field: 'id', headerName: 'ID', width: 100 },
    { field: 'name', headerName: 'Name', width: 220 },
    {
      field: 'type', headerName: 'Type', width: 120,
      renderCell: (row) => (
        <Chip label={row.type} size="small"
          sx={{
            fontWeight: 600, fontSize: '0.75rem', borderRadius: '6px',
            bgcolor: row.type === 'Corporate' ? '#F3E5F5' : '#E3F2FD',
            color: row.type === 'Corporate' ? '#7B1FA2' : '#1565C0',
          }}
        />
      ),
    },
    { field: 'email', headerName: 'Email', width: 220 },
    { field: 'phone', headerName: 'Phone', width: 150 },
    { field: 'submittedDate', headerName: 'Joined', width: 120 },
    {
      field: 'status', headerName: 'Status', width: 120,
      renderCell: () => <StatusBadge status="Active" />,
    },
  ];

  const rejectedColumns = [
    { field: 'name', headerName: 'Name', width: 220 },
    {
      field: 'type', headerName: 'Type', width: 120,
      renderCell: (row) => (
        <Chip label={row.type} size="small"
          sx={{
            fontWeight: 600, fontSize: '0.75rem', borderRadius: '6px',
            bgcolor: row.type === 'Corporate' ? '#F3E5F5' : '#E3F2FD',
            color: row.type === 'Corporate' ? '#7B1FA2' : '#1565C0',
          }}
        />
      ),
    },
    { field: 'reason', headerName: 'Rejection Reason', width: 250 },
    { field: 'submittedDate', headerName: 'Submitted', width: 130 },
    { field: 'rejectedDate', headerName: 'Rejected', width: 130 },
    {
      field: 'status', headerName: 'Status', width: 120,
      renderCell: () => <StatusBadge status="Rejected" />,
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY, mb: 1 }}>
        Partner Approval
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review and approve partner / individual agent registration invites
      </Typography>

      {pendingPartners.length === 0 && tab === 0 && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          No pending invites. Go to User Management → "Generate Invite Link" to invite a new partner or agent, then simulate submission.
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <StatCard title="Pending Approvals" value={stats.pending} icon={PersonIcon}
            color="#F57F17" subtitle="awaiting review" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard title="Approved This Month" value={stats.approvedThisMonth} icon={CheckCircleIcon}
            color={SECONDARY} subtitle="vs last month" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard title="Rejection Rate" value={`${stats.rejectionRate}%`} icon={CancelIcon}
            color="#C62828" subtitle="all time" />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
          TabIndicatorProps={{ sx: { bgcolor: PRIMARY } }}>
          <Tab label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Pending
              <Chip label={pendingPartners.length} size="small"
                sx={{ bgcolor: '#FFF8E1', color: '#F57F17', fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
            </Box>
          } sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label={`Approved (${approvedPartners.length})`} sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label={`Rejected (${rejectedPartners.length})`} sx={{ textTransform: 'none', fontWeight: 600 }} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {tab === 0 && (
        <DataTable title="Pending Partner Applications" columns={pendingColumns} rows={pendingPartners} searchable />
      )}
      {tab === 1 && (
        <DataTable title="Approved Partners & Agents" columns={approvedColumns} rows={approvedPartners} searchable exportable />
      )}
      {tab === 2 && (
        <DataTable title="Rejected Applications" columns={rejectedColumns} rows={rejectedPartners} searchable />
      )}

      {/* View Details Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem', color: PRIMARY }}>
            Partner Registration Details
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPartner && (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ width: 56, height: 56, bgcolor: `${PRIMARY}15`, color: PRIMARY }}>
                  {selectedPartner.type === 'Corporate' ? <BusinessIcon /> : <PersonIcon />}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedPartner.name}</Typography>
                  <Chip label={selectedPartner.type} size="small"
                    sx={{
                      fontWeight: 600, fontSize: '0.75rem', borderRadius: '6px',
                      bgcolor: selectedPartner.type === 'Corporate' ? '#F3E5F5' : '#E3F2FD',
                      color: selectedPartner.type === 'Corporate' ? '#7B1FA2' : '#1565C0',
                    }}
                  />
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <List disablePadding>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}><EmailIcon sx={{ fontSize: 20, color: PRIMARY }} /></ListItemIcon>
                  <ListItemText primary="Email" secondary={selectedPartner.email} />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}><PhoneIcon sx={{ fontSize: 20, color: PRIMARY }} /></ListItemIcon>
                  <ListItemText primary="Phone" secondary={selectedPartner.phone} />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}><CalendarTodayIcon sx={{ fontSize: 20, color: PRIMARY }} /></ListItemIcon>
                  <ListItemText primary="Submitted Date" secondary={selectedPartner.submittedDate} />
                </ListItem>
                {selectedPartner.icNumber && selectedPartner.icNumber !== '—' && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}><PersonIcon sx={{ fontSize: 20, color: PRIMARY }} /></ListItemIcon>
                    <ListItemText primary="IC Number" secondary={selectedPartner.icNumber} />
                  </ListItem>
                )}
                {selectedPartner.company && selectedPartner.company !== '—' && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}><BusinessIcon sx={{ fontSize: 20, color: PRIMARY }} /></ListItemIcon>
                    <ListItemText primary="Company" secondary={selectedPartner.company} />
                  </ListItem>
                )}
              </List>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Uploaded Documents</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(selectedPartner.documents || []).map((doc) => (
                  <Chip key={doc} icon={<DescriptionIcon sx={{ fontSize: 14 }} />}
                    label={doc} size="small" variant="outlined" sx={{ borderRadius: '6px' }} />
                ))}
              </Box>
              {selectedPartner.referredBy && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Source / Note</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedPartner.referredBy}</Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDetailOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
          {selectedPartner?.status === 'submitted' && (
            <>
              <Button variant="contained" color="error" startIcon={<CancelIcon />}
                onClick={() => handleRejectOpen(selectedPartner)}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
                Reject
              </Button>
              <Button variant="contained" startIcon={<CheckCircleIcon />}
                onClick={() => handleApprove(selectedPartner)}
                sx={{ bgcolor: SECONDARY, textTransform: 'none', fontWeight: 600, borderRadius: 2, '&:hover': { bgcolor: '#00a383' } }}>
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Reject reason dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.15rem' }}>Reject Application</Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Rejecting <strong>{selectedPartner?.name}</strong>. Please provide a reason:
          </Typography>
          <TextField fullWidth multiline rows={3} size="small"
            placeholder="e.g., Incomplete documentation, failed identity verification..."
            value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRejectDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRejectConfirm}
            sx={{ borderRadius: 2, textTransform: 'none' }}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}
          sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
